import React, { useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { ProjectWithStats, projectV2ShowCompletedTasksAtom } from '../../../../atoms/v3/projectV2Atoms';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { ProjectItemV2 } from './ProjectItemV2';
import { TaskItemV2List } from '../tasks/TaskItemV2List';
import { DroppableArea } from '../tasks/DroppableArea';
import { QuickAddBarV2 } from './QuickAddBarV2';

interface ProjectCardV2Props {
  project: ProjectWithStats;
  mode: 'expanded' | 'card'; // 展开模式或卡片模式
  onProjectClick: (projectId: string) => void;
  onTaskClick: (taskId: string) => void;
  onTaskToggle: (taskId: string, completed: boolean) => void;
  onTaskUpdate?: (taskId: string, updates: { projectId?: string; vital?: boolean; urgent?: boolean }) => void;
  showQuickAddButton?: boolean; // 是否显示快速添加按钮
  onQuickAddClick?: (projectId: string) => void; // 快速添加按钮点击回调
  activeQuickAddProjectId?: string | null; // 当前激活的QuickAdd项目ID
  onQuickAddClose?: () => void; // 关闭QuickAdd回调
  onTaskCreated?: () => void; // 任务创建成功回调
}

/**
 * ProjectV2 项目卡片容器组件
 * 包含项目信息 + 任务列表
 */
export const ProjectCardV2: React.FC<ProjectCardV2Props> = ({
  project,
  mode,
  onProjectClick,
  onTaskClick,
  onTaskToggle,
  onTaskUpdate,
  showQuickAddButton = false,
  onQuickAddClick,
  activeQuickAddProjectId,
  onQuickAddClose,
  onTaskCreated
}) => {
  const { t } = useTranslation(['v3/projects', 'v3/tasks']);
  const [isDark] = useAtom(isDarkAtom);
  const [showCompletedTasks] = useAtom(projectV2ShowCompletedTasksAtom);

  // 处理项目点击
  const handleProjectClick = useCallback(() => {
    onProjectClick(project.id);
  }, [onProjectClick, project.id]);

  // 处理任务点击
  const handleTaskClick = useCallback((taskId: string) => {
    onTaskClick(taskId);
  }, [onTaskClick]);

  // 处理任务状态切换
  const handleTaskToggle = useCallback((taskId: string, completed: boolean) => {
    onTaskToggle(taskId, completed);
  }, [onTaskToggle]);

  // 获取容器样式
  const getContainerStyle = () => {
    if (mode === 'card') {
      // 卡片模式：有边框阴影
      return `
        rounded-xl shadow-sm border transition-all duration-200
        ${isDark 
          ? 'bg-ios-dark-gray6 border-ios-dark-gray4 hover:shadow-md' 
          : 'bg-white border-gray-200 hover:shadow-md'
        }
      `;
    } else {
      // 展开模式：无边框
      return 'bg-transparent';
    }
  };

  // 获取内容样式
  const getContentStyle = () => {
    return mode === 'card' ? 'p-2' : 'p-0';
  };

  // 根据开关状态过滤已完成任务
  const filteredTasks = useMemo(() => {
    return project.tasks.filter(task => showCompletedTasks || !task.done);
  }, [project.tasks, showCompletedTasks]);

  // 限制显示的任务数量
  const maxTasks = mode === 'card' ? 12 : 10; // 卡片模式显示更多任务
  const displayTasks = filteredTasks.slice(0, maxTasks);
  const hasMoreTasks = filteredTasks.length > maxTasks;

  return (
    <div
      id={`project-${project.id}`}
      className={getContainerStyle()}
    >
      <div className={getContentStyle()}>
        {/* 项目头部 */}
        <ProjectItemV2
          project={project}
          onClick={handleProjectClick}
          showStats={mode === 'expanded'} // 卡片模式下不显示详细统计
          showQuickAddButton={showQuickAddButton}
          onQuickAddClick={onQuickAddClick}
        />

        {/* QuickAdd 组件 - 位于项目header和任务列表之间 */}
        {showQuickAddButton && (
          <QuickAddBarV2
            isOpen={activeQuickAddProjectId === project.id}
            onClose={onQuickAddClose || (() => {})}
            defaultProjectId={project.id}
            onTaskCreated={onTaskCreated}
          />
        )}

        {/* 分割线 - 只在有任务时显示 */}
        {displayTasks.length > 0 && mode !== 'expanded' && (
          <div className={`
            my-1 border-t
            ${isDark ? 'border-ios-dark-gray4' : 'border-gray-200'}
          `} />
        )}

        {/* 任务列表 */}
        {displayTasks.length > 0 ? (
          <DroppableArea
            areaId={project.id}
            data={{
              type: 'project',
              projectId: project.id
            }}
          >
            <div className="space-y-0">
              <TaskItemV2List
                tasks={displayTasks}
                onToggle={handleTaskToggle}
                onClick={handleTaskClick}
                onTaskUpdate={onTaskUpdate}
                compact={mode === 'card'} // 卡片模式使用紧凑任务项
                viewMode="list"
                enableDrag={true}
                dropZoneType="project"
              />

              {/* 更多任务提示 */}
              {hasMoreTasks && (
                <div
                  className={`
                    px-4 py-2 text-center cursor-pointer transition-colors
                    ${isDark
                      ? 'text-ios-dark-gray3 hover:text-ios-dark-gray2'
                      : 'text-gray-500 hover:text-gray-700'
                    }
                  `}
                  onClick={handleProjectClick}
                >
                  <span className="text-sm">
                    {t('v3/projects:stats.moreTasksHint', {
                      count: filteredTasks.length - maxTasks,
                      defaultValue: `还有 {{count}} 个任务...`
                    })}
                  </span>
                </div>
              )}
            </div>
          </DroppableArea>
        ) : (
          // 空状态
          <div className={`
            text-center py-4
            ${isDark ? 'text-ios-dark-gray3' : 'text-gray-500'}
          `}>
            <span className="text-sm">
              {t('v3/projects:emptyState.noTasks')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
